import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchDelete, fetchGet } from '../../store/fetch';
import { IStoreState } from '../../store/IStoreState';
import { openApiFetchGet } from '../../store/openApiFetch';
import { ObjectStates } from '../../utils/enums/ObjectStates';
import { Roles } from '../../utils/enums/Roles';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import { SortColumn, SortDirections } from '../../utils/sorting';
import { IconEditButton } from '../common/iconButtons/IconEditButton';
import { IconRemoveButton } from '../common/iconButtons/IconRemoveButton';
import {
    EditPanel,
    EditPanelButtons,
    EditPanelCell,
    EditPanelFilterMultiSelect,
    EditPanelInput,
    EditPanelRow,
    ISelectOption,
    PanelBody,
    PanelButtonLink,
    PanelHeading,
    PanelRouterLink,
    PanelWithoutForm,
    Table,
    TableBody,
    TableButtons,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
    TableSortableHeader,
} from '../elements';
import { Spinner } from '../spinners';
import { transformLabelsToSelectOptions } from '../utils/entityLabels.utils';
import { ListPager } from '../utils/ListPager';
import { NullableFormattedDate } from '../utils/NullableFormattedDate';
import RoleDependent from '../utils/RoleDependent';
import { SurveyStatusLabel } from '../utils/SurveyStatusLabel';
import { TagLabels } from '../utils/TagLabels';
import { useReactAsync } from '../utils/useReactAsync';
import { useSearch } from '../utils/useSearch';
import { FormatCondition } from './FormatCondition';
import { FormatRisk } from './FormatRisk';

type Pagination = { limit: string; offset: string; sortColumns: SortColumn[] };
type Props = {
    objectId: string;
    objectStatus: ObjectStates;
    isPublic: boolean;
    canBatchAddSurvey: boolean;
    useProductTitle?: boolean | undefined;
};

const allowedRoles = [Roles.Administrator, Roles.Operator, Roles.Surveyor, Roles.Client];

const loadSurveys = async (params: { objectId: string; query: Pagination }) => {
    const result = await openApiFetchGet('/api/objects/{objectId}/surveysPaged', { objectId: params.objectId, ...params.query });
    return result;
};

const loadLabelsWithinObjectSurveys = async (objectId: string): Promise<string[]> => {
    const url = `/api/objects/${objectId}/surveys/labels`;
    const result = await fetchGet(url);
    return result.json as string[];
};

export function ObjectSurveyList(props: Props) {
    const translate = useTranslate();
    const navigate = useNavigate();

    const areLabelsVisibleInOverviews = useSelector((state: IStoreState) => state.authentication.areLabelsVisibleInOverviews);

    const [selectedLabels, setSelectedLabels] = React.useState<string[]>([]);
    const [isLabelFilteringShown, setIsLabelFilteringShown] = React.useState<boolean>(false);
    const [knownLabels, setKnownLabels] = React.useState<ISelectOption[]>([]);
    const [description, setDescription] = React.useState<string>('');
    const [isShowingAll, setisShowingAll] = React.useState<boolean>(false);
    const itemsPerPage = isShowingAll ? 2000 : 20;
    const { handleSortChanged, handleNavigation, queryObject, searchParameters, setSearchAttribute } = useSearch(
        {},
        [{ columnName: 'surveryedOn', direction: SortDirections.Descending }],
        itemsPerPage,
    );

    const args = { query: queryObject, objectId: props.objectId };
    const { data, reload, isLoading } = useReactAsync(loadSurveys, args, [queryObject]);

    const deleteSurvey = async (surveyId: string) => {
        await fetchDelete(`/api/surveys/${surveyId}`);
        await reload();
    };

    React.useEffect(() => {
        const loadAndSaveLabels = async () => {
            const labels = await loadLabelsWithinObjectSurveys(props.objectId);
            const options = transformLabelsToSelectOptions(labels);
            setKnownLabels(options);
        };

        void loadAndSaveLabels();
    }, [props.objectId]);

    const toggleIsLabelFilteringShown = () => {
        setIsLabelFilteringShown((value) => !value);
    };

    const handleLabelOptionSelected = (labels: string[]): void => {
        setSearchAttribute({ labels });
        setSelectedLabels(labels);
    };

    const handleDescriptionEnter = () => {
        setSearchAttribute({ description });
    };

    const handleShowAllClick = () => {
        setisShowingAll(!isShowingAll);
        handleNavigation(1);
    };

    return (
        <PanelWithoutForm id="Maintenance">
            <PanelHeading text={props.useProductTitle ? translate('object.productList') : translate('object.surveyList')} icon="fa fa-camera">
                <PanelButtonLink onClick={toggleIsLabelFilteringShown} mb0={true} text="filter" icon="fa fa-search" small={true} />
                {isCustomerVersion('zxy') && (
                    <RoleDependent
                        allowed={[Roles.Administrator]}
                        content={
                            <PanelRouterLink
                                to={`/objects/${props.objectId}/importsurvey`}
                                text={
                                    props.useProductTitle
                                        ? translate('common.importItem', translate('common.product'))
                                        : translate('common.importItem', translate('common.survey'))
                                }
                                icon="fa fa-upload"
                                small
                            />
                        }
                    />
                )}

                {/* Add Survey */}
                {props.objectStatus !== ObjectStates.Deleted && (
                    <RoleDependent
                        allowed={allowedRoles}
                        isNotAuthorized={!props.canBatchAddSurvey}
                        isPublic={props.isPublic}
                        content={
                            <PanelRouterLink
                                to={`/surveys/add?objectId=${props.objectId}`}
                                text={
                                    props.useProductTitle
                                        ? translate('common.addItem', translate('common.product'))
                                        : translate('common.addItem', translate('common.survey'))
                                }
                                icon="fa fa-plus"
                                small
                                primary
                            />
                        }
                    />
                )}
            </PanelHeading>

            <PanelBody noPadding={true} whiteBackground={true} isForm={true}>
                {isLabelFilteringShown && (
                    <EditPanel bottomMargin={false} noPadding={false}>
                        <EditPanelRow>
                            <EditPanelCell rowCount={6}>
                                <EditPanelFilterMultiSelect
                                    topLabel={props.useProductTitle ? translate('product.filterOnLabel') : translate('survey.filterOnLabel')}
                                    name="labels"
                                    placeholder={translate('common.selectItem', translate('common.labels'))}
                                    value={selectedLabels}
                                    options={knownLabels}
                                    handleChange={handleLabelOptionSelected}
                                />
                            </EditPanelCell>
                            <EditPanelCell rowCount={6}>
                                <EditPanelInput
                                    topLabel={
                                        props.useProductTitle ? translate('product.filterOnDescription') : translate('survey.filterOnDescription')
                                    }
                                    name="description"
                                    placeholder={translate('common.searchText')}
                                    value={description}
                                    handleChange={setDescription}
                                    handleEnter={handleDescriptionEnter}
                                />
                            </EditPanelCell>
                        </EditPanelRow>
                    </EditPanel>
                )}

                <Table hover={true}>
                    <TableHead>
                        <tr className="bg-light">
                            <TableSortableHeader
                                header={translate('common.date')}
                                columnName="surveys.surveryedOn"
                                sortColumns={searchParameters.sortColumns}
                                onClick={handleSortChanged}
                            />
                            <TableSortableHeader
                                header={props.useProductTitle ? translate('product.description') : translate('survey.description')}
                                columnName="surveys.description"
                                sortColumns={searchParameters.sortColumns}
                                onClick={handleSortChanged}
                            />

                            <TableSortableHeader
                                header={props.useProductTitle ? translate('common.template') : translate('common.inspectionTemplate')}
                                columnName="inspectionStandardType"
                                sortColumns={searchParameters.sortColumns}
                                onClick={handleSortChanged}
                            />

                            <TableSortableHeader
                                header={translate('common.result')}
                                columnName="surveys.condition"
                                sortColumns={searchParameters.sortColumns}
                                onClick={handleSortChanged}
                            />

                            <TableSortableHeader
                                header={translate('common.status')}
                                columnName="surveys.status"
                                sortColumns={searchParameters.sortColumns}
                                onClick={handleSortChanged}
                            />

                            <TableHeader>&nbsp;</TableHeader>
                        </tr>
                    </TableHead>

                    <TableBody>
                        {isLoading && (
                            <TableRow>
                                <td colSpan={6} className="text-center">
                                    <Spinner />
                                </td>
                            </TableRow>
                        )}

                        {!isLoading && (
                            <>
                                {data?.items.map((item) => (
                                    <TableRow key={item.id} onClick={() => navigate(`/surveys/${item.id}?objectId=${props.objectId}`)}>
                                        <TableData nowrap={true}>
                                            <NullableFormattedDate value={item.surveryedOn} />
                                        </TableData>
                                        <TableData>
                                            {item.description}&nbsp;
                                            {areLabelsVisibleInOverviews && <TagLabels labels={item.labels} />}
                                        </TableData>
                                        <TableData nowrap={true}>
                                            {item.inspectionStandardType && (
                                                <span>{translate(`inspectionStandard.${item.inspectionStandardType}`)}</span>
                                            )}
                                        </TableData>
                                        <TableData nowrap={true}>
                                            <FormatCondition
                                                condition={item.condition}
                                                careCondition={item.careCondition}
                                                inspectionStandardType={item.inspectionStandardType}
                                            />{' '}
                                            &nbsp;
                                            <FormatRisk
                                                craInspectionCondition={item.craInspectionCondition}
                                                craInitialHistoryCondition={item.craInitialHistoryCondition}
                                            />
                                        </TableData>
                                        <TableData>
                                            <SurveyStatusLabel value={item.status} />
                                        </TableData>
                                        <TableButtons>
                                            {item.canEdit && <IconEditButton onClick={() => navigate(`/surveys/${item.id}/summaryAndAdvice`)} />}
                                            {item.canDelete && (
                                                <IconRemoveButton
                                                    removeQuestion={translate('common.confirmDelete', item.description)}
                                                    onClick={() => void deleteSurvey(item.id)}
                                                />
                                            )}
                                        </TableButtons>
                                    </TableRow>
                                ))}
                            </>
                        )}
                    </TableBody>
                </Table>
            </PanelBody>

            {(data?.total ?? 0) > itemsPerPage && (
                <EditPanelButtons footer>
                    <PanelButtonLink
                        onClick={handleShowAllClick}
                        text={isShowingAll ? translate('common.showLess') : translate('common.showAll')}
                        small={true}
                    />
                    <ListPager
                        disabled={isLoading}
                        itemsPerPage={itemsPerPage}
                        inPanel={true}
                        total={data?.total ?? 0}
                        page={searchParameters.page}
                        handleNavigation={handleNavigation}
                        mirrorQueryParams={true}
                        navigationTitles={true}
                    />
                </EditPanelButtons>
            )}
        </PanelWithoutForm>
    );
}
